import React, { useState, useEffect, useRef } from "react";

/* ---------- Rich text uit de database ---------- */
export const Rich = ({ html, as: As = "div", ...rest }) => (
  <As {...rest} dangerouslySetInnerHTML={{ __html: html || "" }} />
);

/* ---------- Reveal on scroll ---------- */
export function Reveal({ children, stagger = false, className = "", as: As = "div", ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const checkInView = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top < vh * 0.95 && rect.bottom > 0) {
        el.classList.add("in");
        return true;
      }
      return false;
    };
    if (checkInView()) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.classList.add("in");
            io.unobserve(el);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -5% 0px" }
    );
    io.observe(el);
    const t = setTimeout(() => el.classList.add("in"), 600);
    return () => {
      io.disconnect();
      clearTimeout(t);
    };
  }, []);
  return (
    <As ref={ref} className={`${stagger ? "reveal-stagger" : "reveal"} ${className}`} {...rest}>
      {children}
    </As>
  );
}

/* ---------- Ambient canvas hero loop ---------- */
export function AmbientCanvas({ mode = "dust" }) {
  const ref = useRef(null);
  useEffect(() => {
    const motion = document.body.dataset.motion || "high";
    if (motion === "low") return;
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0, h = 0, running = true;
    const particles = [];

    function resize() {
      const rect = canvas.getBoundingClientRect();
      w = rect.width; h = rect.height;
      canvas.width = w * DPR; canvas.height = h * DPR;
      ctx.scale(DPR, DPR);
    }
    resize();
    const onResize = () => { ctx.setTransform(1, 0, 0, 1, 0, 0); resize(); };
    window.addEventListener("resize", onResize);

    const COUNT = mode === "rain" ? 180 : 90;
    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * w, y: Math.random() * h,
        r: Math.random() * 1.6 + 0.3,
        vy: (Math.random() * 0.18 + 0.04) * (mode === "rain" ? 1.8 : 1),
        vx: (Math.random() - 0.5) * 0.05,
        a: Math.random() * 0.6 + 0.15,
        tw: Math.random() * Math.PI * 2,
      });
    }

    let raf;
    function tick() {
      if (!running) return;
      ctx.clearRect(0, 0, w, h);
      const grad = ctx.createRadialGradient(w * 0.5, h * 0.6, 0, w * 0.5, h * 0.6, Math.max(w, h) * 0.7);
      grad.addColorStop(0, "rgba(201, 169, 97, 0.03)");
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      for (const p of particles) {
        p.y += p.vy; p.x += p.vx; p.tw += 0.02;
        if (p.y > h + 10) { p.y = -10; p.x = Math.random() * w; }
        if (p.x > w + 10) p.x = -10;
        if (p.x < -10) p.x = w + 10;
        const alpha = p.a * (0.6 + 0.4 * Math.sin(p.tw));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201, 169, 97, ${alpha.toFixed(3)})`;
        ctx.shadowBlur = 8; ctx.shadowColor = "rgba(201, 169, 97, 0.4)";
        ctx.fill();
      }
      ctx.shadowBlur = 0;
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [mode]);
  return (
    <canvas ref={ref} className="hero-canvas" style={{
      position: "absolute", inset: 0, width: "100%", height: "100%",
      pointerEvents: "none", opacity: 0.9, transition: "opacity .6s ease",
    }} />
  );
}

/* ---------- Beeld & Geluid mark ---------- */
export function BGMark({ size = 24, color = "currentColor", opacity = 1, gap = 0.18 }) {
  const s = size;
  const pad = 0.04;
  const cgap = s * gap;
  const sq = (s * (1 - 2 * pad) - cgap) / 2;
  const r = sq / 2;
  const left = s * pad;
  const top = (s - sq) / 2;
  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} aria-hidden="true" style={{
      display: "inline-block", opacity, flexShrink: 0, verticalAlign: "middle", overflow: "visible",
    }}>
      <rect x={left} y={top} width={sq} height={sq} fill={color} />
      <circle cx={left + sq + cgap + r} cy={s / 2} r={r} fill={color} />
    </svg>
  );
}

export function BGOrnament({ tone = "gold", size = 18, align = "center", label }) {
  const color = tone === "gold" ? "var(--gold)"
    : tone === "gold-deep" ? "var(--gold-deep)"
    : tone === "cream" ? "rgba(243,234,215,0.55)"
    : tone === "ink" ? "rgba(26,22,17,0.45)" : tone;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14,
      justifyContent: align === "center" ? "center" : "flex-start", color,
    }}>
      <BGMark size={size} color={color} opacity={0.7} />
      {label && (
        <span style={{
          fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.22em",
          textTransform: "uppercase", opacity: 0.85,
        }}>{label}</span>
      )}
      <BGMark size={size} color={color} opacity={0.7} />
    </div>
  );
}

export function InstagramMark({ size = 18, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1" fill={color} stroke="none" />
    </svg>
  );
}

/* ---------- Countdown ---------- */
export function LaunchCountdown({ tone = "gold", target: targetIso }) {
  const target = new Date(targetIso || "2026-09-14T17:30:00+02:00").getTime();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((diff / (1000 * 60)) % 60);
  const secs = Math.floor((diff / 1000) % 60);
  const pad = (n) => String(n).padStart(2, "0");
  const c = tone === "gold" ? "var(--gold)" : "var(--cream-ink)";
  return (
    <div data-testid="swof-countdown" style={{ display: "flex", gap: 28, fontFamily: "var(--mono)", color: c, alignItems: "baseline" }}>
      {[["dagen", days], ["uur", pad(hours)], ["min", pad(mins)], ["sec", pad(secs)]].map(([label, val], i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "baseline" }}>
          <span style={{
            fontSize: "clamp(28px, 3vw, 44px)", fontFeatureSettings: '"tnum"',
            fontWeight: 300, letterSpacing: "-0.01em",
          }}>{val}</span>
          <span style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", opacity: 0.6 }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------- Placeholder-portret: monogram + label ---------- */
export function PortraitSlot({ name = "", ratio = "4 / 5", label = "foto volgt", monogram, src, alt, varianten, focus }) {
  const initials = monogram !== undefined ? monogram
    : name.split(" ").filter((w) => w[0] === w[0].toUpperCase()).map((w) => w[0]).join("").slice(0, 2);
  const srcSet = varianten
    ? Object.entries(varianten).map(([breedte, url]) => `${url} ${breedte}w`).join(", ")
    : undefined;
  return (
    <div style={{
      position: "relative", aspectRatio: ratio, width: "100%",
      background: "linear-gradient(165deg, #221c14 0%, #100d09 100%)",
      border: "1px solid var(--line)",
      display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
    }}>
      {src ? (
        <img src={src} srcSet={srcSet} sizes="(max-width: 900px) 50vw, 25vw" alt={alt || name}
          loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: focus || "center 18%" }} />
      ) : (
        <>
          <span style={{
            fontFamily: "var(--serif)", fontSize: "clamp(38px, 4vw, 64px)",
            color: "rgba(201,169,97,0.32)", letterSpacing: "0.04em", lineHeight: 1,
          }}>{initials}</span>
          <span style={{
            position: "absolute", bottom: 10, left: 12,
            fontFamily: "var(--mono)", fontSize: 9, letterSpacing: "0.2em",
            textTransform: "uppercase", color: "rgba(243,234,215,0.34)",
          }}>{label}</span>
        </>
      )}
    </div>
  );
}

/* ---------- Header / nav ---------- */
export function Header({ show }) {
  return (
    <header className="site-header">
      <a href="#top" className="wordmark" data-testid="swof-wordmark">
        <span className="dot" />
        SWOF
      </a>
      <nav className="nav-list">
        {show.onderscheiding && <a href="#onderscheiding">De onderscheiding</a>}
        {show.winnaars && <a href="#winnaars" className="nav-winnaars" data-testid="nav-winnaars">Winnaars</a>}
        {show.bestuur && <a href="#bestuur">Bestuur</a>}
        {show.gala && <a href="#gala">Gala</a>}
        {show.plek && <a href="#plek">De plek</a>}
        {show.fotogalerie && (
          <a href="#fotogalerie" className="nav-fotogalerie" data-testid="nav-fotogalerie"
            onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("swof:open-galerie")); }}>
            Fotogalerie
          </a>
        )}
        {show.signup && <a href="#update" style={{ color: "var(--gold)" }}>Blijf op de hoogte →</a>}
      </nav>
    </header>
  );
}

/* ---------- Footer (vast, geen blok) ---------- */
function FooterCol({ title, items }) {
  return (
    <div className="footer-col" style={{ gridColumn: "span 3" }}>
      <div className="eyebrow" style={{ color: "inherit", opacity: 0.6, marginBottom: 16 }}>{title}</div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontFamily: "var(--sans)", fontSize: 14, lineHeight: 2 }}>
        {items.map((it, i) => <li key={i} style={{ opacity: 0.85 }}>{it}</li>)}
      </ul>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer style={{ background: "var(--ink)", color: "var(--cream)", borderTop: "1px solid var(--line)" }}>
      <div className="container" style={{ padding: "72px clamp(24px, 4vw, 72px) 40px" }}>
        <div className="footer-cols" style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 32 }}>
          <div className="footer-brand" style={{ gridColumn: "span 6" }}>
            <div style={{ fontFamily: "var(--serif)", fontSize: "clamp(36px, 4vw, 56px)", lineHeight: 1.02, marginBottom: 20 }}>
              Songwriters<br />Wall of Fame
            </div>
            <p style={{ fontFamily: "var(--sans)", fontSize: 14, lineHeight: 1.7, opacity: 0.7, maxWidth: 380, margin: 0 }}>
              Een permanent eerbetoon aan de componisten en tekstdichters die minimaal 20 jaar actief
              zijn als songwriter én een rijke staat van dienst hebben met meerdere successen en hits.
              Eerste editie: 14 september 2026, Beeld &amp; Geluid, Hilversum.
            </p>
            <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 10, opacity: 0.55 }}>
              <BGMark size={14} color="var(--gold)" opacity={0.9} />
              <span style={{ fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase" }}>
                te gast bij Beeld &amp; Geluid
              </span>
            </div>
          </div>
          <FooterCol title="Bezoek" items={["Beeld & Geluid", "Media Parkboulevard 1", "1217 WE Hilversum", "Gala: 14 sept. 2026"]} />
          <div className="footer-col" style={{ gridColumn: "span 3" }}>
            <div className="eyebrow" style={{ color: "inherit", opacity: 0.6, marginBottom: 16 }}>Media</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, fontFamily: "var(--sans)", fontSize: 14, lineHeight: 2 }}>
              {["Persmap", "Beeldmateriaal", "info@songwriterswalloffame.nl"].map((it, i) => (
                <li key={i} style={{ opacity: 0.85 }}>{it}</li>
              ))}
            </ul>
            <a href="https://www.instagram.com/songwriterswalloffame/" target="_blank" rel="noopener noreferrer"
              className="ig-link" style={{
                marginTop: 18, display: "inline-flex", alignItems: "center", gap: 10,
                color: "inherit", textDecoration: "none", fontFamily: "var(--mono)", fontSize: 11,
                letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.75,
                transition: "opacity .3s var(--ease), color .3s var(--ease)",
              }}>
              <InstagramMark size={18} color="var(--gold)" />
              <span>@songwriterswalloffame</span>
            </a>
          </div>
        </div>
        <div style={{
          marginTop: 64, paddingTop: 24, borderTop: "1px solid var(--line)",
          display: "flex", justifyContent: "space-between", fontFamily: "var(--mono)",
          fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase",
          opacity: 0.55, flexWrap: "wrap", gap: 16,
        }}>
          <span>© 2026 Stichting Songwriters Wall of Fame</span>
          <span>Nederland · EST. 2026</span>
        </div>
      </div>
    </footer>
  );
}

/* ---------- Staande foto's: focus op de persoon (onderste deel) ---------- */
export const fotoFocus = (m) => (m && m.hoogte > m.breedte ? "center 38%" : "center");

/* ---------- Lightbox met pijltjes ---------- */
export function Lightbox({ items, index, onClose, onChange, testId = "lightbox", zIndex = 400 }) {
  const n = items.length;
  const ga = React.useCallback((d) => onChange((index + d + n) % n), [index, n, onChange]);
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") ga(1);
      else if (e.key === "ArrowLeft") ga(-1);
      else if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ga, onClose]);
  const m = items[index];
  const pijl = {
    position: "absolute", top: "50%", transform: "translateY(-50%)", zIndex: 2,
    width: 52, height: 52, borderRadius: "50%", border: "1px solid rgba(201,169,97,0.5)",
    background: "rgba(5,4,3,0.6)", color: "var(--gold)", fontSize: 22, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  };
  return (
    <div onClick={onClose} data-testid={testId} style={{
      position: "fixed", inset: 0, zIndex, background: "rgba(5,4,3,0.94)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "clamp(16px, 4vw, 64px)", cursor: "zoom-out",
    }}>
      <button onClick={(e) => { e.stopPropagation(); ga(-1); }} aria-label="Vorige foto"
        data-testid={`${testId}-vorige`} style={{ ...pijl, left: "clamp(8px, 2vw, 32px)" }}>‹</button>
      <button onClick={(e) => { e.stopPropagation(); ga(1); }} aria-label="Volgende foto"
        data-testid={`${testId}-volgende`} style={{ ...pijl, right: "clamp(8px, 2vw, 32px)" }}>›</button>
      <figure onClick={(e) => e.stopPropagation()} style={{ margin: 0, maxWidth: "min(1200px, 100%)", cursor: "default", textAlign: "center" }}>
        {m?.url && (
          <img key={m.id} src={m.url} alt="" data-testid={`${testId}-img`} style={{
            maxWidth: "100%", maxHeight: "78vh", width: "auto", height: "auto",
            objectFit: "contain", border: "1px solid var(--line)", display: "block", margin: "0 auto",
          }} />
        )}
        <figcaption style={{
          marginTop: 14, display: "flex", justifyContent: "space-between", gap: 24,
          fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.2em",
          textTransform: "uppercase", color: "rgba(243,234,215,0.55)",
        }}>
          <span>{`${index + 1} / ${n}`}</span>
          <span>← → · Esc</span>
        </figcaption>
      </figure>
    </div>
  );
}
